import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Database, Lock, Shield } from 'lucide-react';

const problems = [
  {
    icon: AlertTriangle,
    title: "Adversarial Attacks",
    description: "Sophisticated manipulations that deceive medical AI models, causing critical failures in diagnostic AI and clinical decision support.",
    stat: "78%",
    statLabel: "vulnerable systems"
  },
  {
    icon: Database,
    title: "Data Poisoning",
    description: "Malicious corruption of medical training datasets that degrades diagnostic accuracy and introduces hidden backdoors.",
    stat: "71%",
    statLabel: "face integrity threats"
  },
  {
    icon: Lock,
    title: "Model Theft",
    description: "Unauthorized access and duplication of proprietary medical AI models leading to IP theft and regulatory violations.",
    stat: "$6.2B",
    statLabel: "annual IP theft"
  },
  {
    icon: Shield,
    title: "Legacy Security Failing",
    description: "Traditional cybersecurity tools weren't designed for healthcare AI-specific threats, leaving massive vulnerabilities.",
    stat: "92%",
    statLabel: "lack AI security"
  }
];

export default function ProblemSectionV2() {
  return (
    <section id="security" className="py-32 bg-black relative overflow-hidden">
      {/* Background effect */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(239,68,68,0.1),transparent_70%)]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-block px-4 py-2 rounded-full border border-red-500/30 bg-red-500/10 text-red-400 text-sm font-medium mb-6">
            Critical Industry Challenge
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6">
            The <span className="text-red-500">Healthcare AI</span>
            <br />Security Crisis
          </h2>
          <p className="text-xl text-slate-400 max-w-3xl mx-auto">
            Lives depend on secure AI systems. The cost of inaction is <span className="text-white font-semibold">$4.3M</span> per breach.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-transparent rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              <div className="relative p-8 rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 group-hover:border-red-500/30 transition-all duration-300">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center border border-red-500/20">
                    <problem.icon className="w-7 h-7 text-red-500" />
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-white">{problem.stat}</p>
                    <p className="text-xs text-slate-500">{problem.statLabel}</p>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-3">{problem.title}</h3>
                <p className="text-slate-400 leading-relaxed">{problem.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}